import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  AccessTimeOutlined,
  GraphicEq,
  HeadphonesOutlined,
  PeopleAltOutlined,
  TaskAltOutlined,
} from "@mui/icons-material";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import useApiEp from "../../hooks/useApiEp";

const COLORS = {
  blue: "#2563eb",
  green: "#16a34a",
  purple: "#7c3aed",
  amber: "#d97706",
  text: "#111827",
  muted: "#6b7280",
  border: "#e5e7eb",
  header: "#f9fafb",
};

const EMPTY_FILTERS = {
  startDate: "",
  endDate: "",
  answer_status: "",
  min_listen_duration: "",
  max_listen_duration: "",
};

const formatDuration = (value) => {
  const seconds = Math.round(Number(value) || 0);
  if (!seconds) return "0s";
  if (seconds < 60) return `${seconds}s`;
  return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
};

const formatNumber = (value) => Number(value || 0).toLocaleString("en-IN");

const roleLabel = (role) =>
  ({
    welcomePId: "Welcome",
    menuPId: "Menu",
    noInputPId: "No input",
    wrongInputPId: "Wrong input",
    thanksPId: "Thank you",
  }[role] || "Voice clip");

const MetricCard = ({ label, value, helper, icon, color }) => (
  <Card
    variant="outlined"
    sx={{ height: "100%", borderRadius: 2.5, borderColor: COLORS.border }}
  >
    <CardContent sx={{ p: 2.25, "&:last-child": { pb: 2.25 } }}>
      <Stack direction="row" justifyContent="space-between" spacing={2}>
        <Box>
          <Typography sx={{ fontSize: 12, color: COLORS.muted, fontWeight: 600 }}>
            {label}
          </Typography>
          <Typography sx={{ fontSize: 25, color: COLORS.text, fontWeight: 750, mt: 0.35 }}>
            {value}
          </Typography>
          <Typography sx={{ fontSize: 11.5, color: COLORS.muted, mt: 0.25 }}>
            {helper}
          </Typography>
        </Box>
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: 2,
            display: "grid",
            placeItems: "center",
            bgcolor: `${color}12`,
            color,
          }}
        >
          {icon}
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

const VoiceClipAnalysis = () => {
  const [draftFilters, setDraftFilters] = useState(EMPTY_FILTERS);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [selectedClip, setSelectedClip] = useState("");

  const query = useMemo(() => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== "") params.set(key, value);
    });
    return params.toString();
  }, [filters]);

  const { data: result = {}, isLoading, isError, error } = useApiEp(
    `/voice/get-voice-clips-stats${query ? `?${query}` : ""}`
  );

  const rows = Array.isArray(result.data) ? result.data : [];
  const visibleRows = selectedClip
    ? rows.filter((row) => String(row.clipId) === selectedClip)
    : rows;
  const summary = result.summary || {};
  const selectedRow = selectedClip ? visibleRows[0] : null;
  const displayedSummary = selectedRow
    ? {
        totalCalls: selectedRow.totalCalls,
        answeredCalls: selectedRow.answeredCalls,
        answerRate:
          selectedRow.totalCalls > 0
            ? Math.round((selectedRow.answeredCalls / selectedRow.totalCalls) * 100)
            : 0,
        avgListenDuration: selectedRow.avgListenDuration,
        estimatedCompleted: selectedRow.estimatedCompleted,
        completionRate: selectedRow.completionRate,
      }
    : summary;

  const chartData = visibleRows.slice(0, 10).map((row) => ({
    name: row.fileName || row.originalFileName || `Clip ${row.clipId}`,
    answered: row.answeredCalls || 0,
    completed: row.estimatedCompleted || 0,
  }));

  const setDraft = (key, value) =>
    setDraftFilters((current) => ({ ...current, [key]: value }));

  const applyFilters = () => {
    setFilters(draftFilters);
    setSelectedClip("");
  };

  const clearFilters = () => {
    setDraftFilters(EMPTY_FILTERS);
    setFilters(EMPTY_FILTERS);
    setSelectedClip("");
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: "#f8fafc", minHeight: "100vh" }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={1}
        sx={{ mb: 2.5 }}
      >
        <Box>
          <Typography sx={{ fontSize: 24, fontWeight: 750, color: COLORS.text }}>
            Voice clip analysis
          </Typography>
          <Typography sx={{ mt: 0.35, fontSize: 13, color: COLORS.muted }}>
            Compare answered calls, estimated listeners, and listening duration for each campaign clip.
          </Typography>
        </Box>
        <Chip
          icon={<GraphicEq />}
          label={`${formatNumber(rows.length)} clips`}
          sx={{ bgcolor: "#eef2ff", color: "#4f46e5", fontWeight: 700 }}
        />
      </Stack>

      <Card variant="outlined" sx={{ borderRadius: 2.5, borderColor: COLORS.border, mb: 2.5 }}>
        <CardContent>
          <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 1.5 }}>Filters</Typography>
          <Grid container spacing={1.5} alignItems="center">
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="From date"
                InputLabelProps={{ shrink: true }}
                value={draftFilters.startDate}
                onChange={(event) => setDraft("startDate", event.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="To date"
                InputLabelProps={{ shrink: true }}
                value={draftFilters.endDate}
                onChange={(event) => setDraft("endDate", event.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Answer status</InputLabel>
                <Select
                  label="Answer status"
                  value={draftFilters.answer_status}
                  onChange={(event) => setDraft("answer_status", event.target.value)}
                >
                  <MenuItem value="">All calls</MenuItem>
                  <MenuItem value="Answered">Answered</MenuItem>
                  <MenuItem value="Not Answered">Not answered</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={3} md={1.5}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Min listened (s)"
                inputProps={{ min: 0 }}
                value={draftFilters.min_listen_duration}
                onChange={(event) => setDraft("min_listen_duration", event.target.value)}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={1.5}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Max listened (s)"
                inputProps={{ min: 0 }}
                value={draftFilters.max_listen_duration}
                onChange={(event) => setDraft("max_listen_duration", event.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Stack direction="row" spacing={1}>
                <Button fullWidth variant="contained" onClick={applyFilters} sx={{ textTransform: "none" }}>
                  Apply
                </Button>
                <Button fullWidth variant="outlined" onClick={clearFilters} sx={{ textTransform: "none" }}>
                  Clear
                </Button>
              </Stack>
            </Grid>
          </Grid>
          <Box sx={{ mt: 1.5, maxWidth: 380 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Voice clip</InputLabel>
              <Select
                label="Voice clip"
                value={selectedClip}
                onChange={(event) => setSelectedClip(event.target.value)}
              >
                <MenuItem value="">All voice clips</MenuItem>
                {rows.map((row) => (
                  <MenuItem key={row.clipId} value={String(row.clipId)}>
                    {row.fileName || row.originalFileName || `Clip ${row.clipId}`} ({formatDuration(row.clipDuration)})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </CardContent>
      </Card>

      {isError && (
        <Alert severity="error" sx={{ mb: 2.5 }}>
          {error?.response?.data?.message || "Could not load voice clip analysis."}
        </Alert>
      )}

      {result.note && (
        <Alert severity="info" sx={{ mb: 2.5 }}>
          {result.note}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <MetricCard label="Call attempts" value={formatNumber(displayedSummary.totalCalls)} helper="Within the selected range" icon={<PeopleAltOutlined />} color={COLORS.blue} />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <MetricCard label="Answered" value={formatNumber(displayedSummary.answeredCalls)} helper={`${displayedSummary.answerRate || 0}% answer rate`} icon={<HeadphonesOutlined />} color={COLORS.green} />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <MetricCard label="Avg. listened" value={formatDuration(displayedSummary.avgListenDuration)} helper="Billing duration per answered call" icon={<AccessTimeOutlined />} color={COLORS.amber} />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <MetricCard label="Estimated completions" value={formatNumber(displayedSummary.estimatedCompleted)} helper={`${displayedSummary.completionRate || 0}% of answered clip exposures`} icon={<TaskAltOutlined />} color={COLORS.purple} />
        </Grid>
      </Grid>

      <Grid container spacing={2.5}>
        <Grid item xs={12} lg={5}>
          <Card variant="outlined" sx={{ borderRadius: 2.5, borderColor: COLORS.border, height: "100%" }}>
            <CardContent>
              <Typography sx={{ fontSize: 15, fontWeight: 700 }}>Top clips by listeners</Typography>
              <Typography sx={{ fontSize: 12, color: COLORS.muted, mb: 2 }}>
                Answered calls compared with estimated full listens
              </Typography>
              {isLoading ? (
                <Box sx={{ height: 300, display: "grid", placeItems: "center" }}><CircularProgress size={30} /></Box>
              ) : chartData.length ? (
                <Box sx={{ width: "100%", height: 310 }}>
                  <ResponsiveContainer>
                    <BarChart data={chartData} layout="vertical" margin={{ left: 12, right: 12 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#eef0f3" />
                      <XAxis type="number" allowDecimals={false} />
                      <YAxis type="category" dataKey="name" width={105} tick={{ fontSize: 11 }} tickFormatter={(value) => value.length > 16 ? `${value.slice(0, 16)}…` : value} />
                      <Tooltip />
                      <Bar dataKey="answered" name="Answered" fill={COLORS.blue} radius={[0, 4, 4, 0]} />
                      <Bar dataKey="completed" name="Estimated completed" fill={COLORS.green} radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              ) : (
                <Box sx={{ height: 300, display: "grid", placeItems: "center" }}>
                  <Typography color="text.secondary">No clip data for these filters.</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={7}>
          <Card variant="outlined" sx={{ borderRadius: 2.5, borderColor: COLORS.border }}>
            <CardContent sx={{ pb: "12px !important" }}>
              <Typography sx={{ fontSize: 15, fontWeight: 700 }}>Clip performance</Typography>
              <Typography sx={{ fontSize: 12, color: COLORS.muted }}>
                Completion is estimated when an answered call lasts at least as long as the clip.
              </Typography>
            </CardContent>
            <TableContainer sx={{ maxHeight: 470 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {["Voice clip", "Clip length", "Answered", "Unique listeners", "Avg. listened", "Completed"].map((label) => (
                      <TableCell key={label} sx={{ bgcolor: COLORS.header, fontSize: 11.5, fontWeight: 700, color: COLORS.muted, whiteSpace: "nowrap" }}>
                        {label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoading ? (
                    <TableRow><TableCell colSpan={6}><LinearProgress /></TableCell></TableRow>
                  ) : visibleRows.length ? (
                    visibleRows.map((row) => (
                      <TableRow key={row.clipId} hover>
                        <TableCell sx={{ minWidth: 205 }}>
                          <Stack direction="row" spacing={1.25} alignItems="center">
                            {row.voiceFileUrl ? <audio controls preload="none" src={row.voiceFileUrl} style={{ width: 105, height: 30 }} /> : <GraphicEq sx={{ color: COLORS.muted }} />}
                            <Box sx={{ minWidth: 0 }}>
                              <Typography noWrap sx={{ maxWidth: 170, fontSize: 12.5, fontWeight: 700 }}>{row.fileName || row.originalFileName || `Clip ${row.clipId}`}</Typography>
                              <Stack direction="row" spacing={0.5} sx={{ mt: 0.35 }}>
                                {(row.roles || []).map((role) => <Chip key={role} label={roleLabel(role)} size="small" sx={{ height: 18, fontSize: 9.5 }} />)}
                              </Stack>
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap", fontWeight: 600 }}>{formatDuration(row.clipDuration)}</TableCell>
                        <TableCell>{formatNumber(row.answeredCalls)}</TableCell>
                        <TableCell>{formatNumber(row.uniqueListeners)}</TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>{formatDuration(row.avgListenDuration)}</TableCell>
                        <TableCell sx={{ minWidth: 130 }}>
                          <Stack spacing={0.5}>
                            <Stack direction="row" justifyContent="space-between">
                              <Typography sx={{ fontSize: 11.5, fontWeight: 700 }}>{formatNumber(row.estimatedCompleted)}</Typography>
                              <Typography sx={{ fontSize: 11.5, color: COLORS.muted }}>{row.completionRate || 0}%</Typography>
                            </Stack>
                            <LinearProgress variant="determinate" value={Math.min(100, Number(row.completionRate) || 0)} sx={{ height: 5, borderRadius: 5 }} />
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow><TableCell colSpan={6} align="center" sx={{ py: 6, color: COLORS.muted }}>No voice clips match the selected filters.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default VoiceClipAnalysis;
